/**
 * PortalLayout — patient-facing app chrome.
 *
 * Distinct from `<AppShell>` (which is the staff-tool dense top+side+main
 * grid, deferred-responsive < 1280px). The patient portal is reached
 * primarily from SMS deep-links on phones, so:
 *   - Centered single-column layout with generous horizontal margins
 *   - No left/right rails — patients have one job, just do it
 *   - Sticky header with the *clinic* name (per the branding decision in
 *     scoping #5: patients trust the clinic name, not the platform vendor)
 *   - Sticky footer with the clinic phone for "I got stuck, call us"
 *
 * `clinicName` and `clinicPhone` are passed in by routes that have already
 * fetched `/payment/me`. The login page passes neither — it falls back to
 * a generic "Patient Payment Portal" wordmark, since at login we don't
 * yet know which clinic the patient belongs to.
 */
import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LogOut, Phone } from 'lucide-react';

import { Button } from '@tensaw/design-system/primitives';

import { useAuth } from './auth/AuthContext';
import { useLogout } from './api/queries';
import { CURRENT_BRAND } from './lib/loginTheme';

interface PortalLayoutProps {
  /** Clinic name to display in the header. */
  clinicName?: string;
  /** Clinic phone for the footer "call us" link. */
  clinicPhone?: string;
  /** Build version for support escalation; surfaced in footer. */
  buildVersion?: string;
  children: ReactNode;
}

export function PortalLayout({
  clinicName,
  clinicPhone,
  buildVersion,
  children,
}: PortalLayoutProps) {
  return (
    <div className="flex min-h-screen flex-col bg-muted/30">
      <Header clinicName={clinicName} />
      <main className="flex-1">
        <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
          {children}
        </div>
      </main>
      <Footer clinicPhone={clinicPhone} buildVersion={buildVersion} />
    </div>
  );
}

function Header({ clinicName }: { clinicName?: string }) {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 print:hidden">
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2">
          {clinicName ? (
            <h1 className="truncate text-base font-semibold tracking-tight text-foreground sm:text-lg">
              {clinicName}
            </h1>
          ) : (
            <span className="text-sm font-medium tracking-tight text-muted-foreground">
              Patient Payment Portal
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:flex">
            <Lock className="h-3 w-3" />
            Secure payment
          </div>
          {/*
            Per QA TC-006/TC-008 ("no logout option available"): the auth
            context and useLogout hook were both built but never wired
            into any rendered UI. The auth context refresh / token
            removal worked, but the user had no in-app way to invoke it.
            Show only when authenticated so this doesn't appear on
            /login (where it would be meaningless).
          */}
          <LogoutButton />
        </div>
      </div>
    </header>
  );
}

/**
 * Logout control rendered in the header. Renders nothing when the
 * patient is not authenticated (e.g. on /login). The button calls the
 * backend logout endpoint best-effort, then clears local storage and
 * redirects to /login regardless of network outcome — per
 * UI_DATA_LAYER §7.
 */
function LogoutButton() {
  const auth = useAuth();
  const navigate = useNavigate();
  const logoutMutation = useLogout();

  if (!auth.isAuthenticated) return null;

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      // Network-result doesn't gate the UX outcome — we clear storage
      // and navigate either way. Backend invalidation is best-effort.
      onSettled: () => {
        auth.signOut();
        navigate('/login', { replace: true });
      },
    });
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleLogout}
      loading={logoutMutation.isPending}
      leadingIcon={<LogOut className="h-3.5 w-3.5" />}
      aria-label="Sign out of the patient portal"
    >
      <span className="hidden sm:inline">Sign out</span>
    </Button>
  );
}

function Footer({
  clinicPhone,
  buildVersion,
}: {
  clinicPhone?: string;
  buildVersion?: string;
}) {
  return (
    <footer className="border-t border-border bg-background/60 print:hidden">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-2 px-4 py-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
          <span className="flex items-center gap-1.5">
            <Lock className="h-3 w-3" />
            HIPAA-compliant · 256-bit encryption · Powered by Stripe
          </span>
        </div>
        <div className="flex items-center gap-3">
          <>
            <p>Billing Inquiries? Call</p>
            {CURRENT_BRAND.billingPhoneDisplay && (
              <a
                href={`tel:${CURRENT_BRAND.billingPhoneDisplay}`}
                className="flex items-center gap-1.5 text-foreground hover:text-primary"
              >
                <Phone className="h-3 w-3" />
                {formatPhone(CURRENT_BRAND.billingPhoneDisplay)}
              </a>
            )}
            {buildVersion && (
              <span className="font-mono text-[10px] opacity-60" aria-hidden>
                {buildVersion}
              </span>
            )}
          </>
        </div>
      </div>
    </footer>
  );
}

/** "2055550100" → "(205) 555-0100". Falls through unchanged on non-10-digit. */
function formatPhone(p: string): string {
  const digits = p.replace(/\D/g, '');
  if (digits.length !== 10) return p;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
